import { defineComponent as _defineComponent } from 'vue'
import { vModelRadio as _vModelRadio, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, renderList as _renderList, vModelSelect as _vModelSelect, vModelText as _vModelText, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7a98669b"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "person-creator-selector" }
const _hoisted_2 = { class: "use-existing" }
const _hoisted_3 = { class: "form-check" }
const _hoisted_4 = { class: "form-check-label" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_7 = { class: "create-new" }
const _hoisted_8 = { class: "create-new-option form-check" }
const _hoisted_9 = { class: "form-check-label" }
const _hoisted_10 = {
  key: 1,
  class: "gender"
}
const _hoisted_11 = { class: "form-label" }
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Gender", -1))
const _hoisted_13 = ["disabled"]
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("option", { value: undefined }, null, -1))
const _hoisted_15 = ["value"]
const _hoisted_16 = {
  key: 2,
  class: "invalid-feedback"
}
const _hoisted_17 = { class: "given" }
const _hoisted_18 = { class: "form-label" }
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Given name", -1))
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "form-label" }
const _hoisted_22 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "Surname", -1))
const _hoisted_23 = ["disabled"]
const _hoisted_24 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "online-only" }, [
  /*#__PURE__*/_createElementVNode("small", null, "The new profile will only exist online.")
], -1))

import { computed, nextTick, ref, watch } from 'vue'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { FamilyRelationshipRole, Gender, ItemType, ViewPerson } from '@/rd/ResearchDataModel'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { DataGroupType } from '@/gp/GroupAdminModel'
import { 
  NewProfileRelationship, 
  createProfilePersonAsync, 
  getNewProfileRelationshipGender, 
  getOrCreateChildRelationshipAsync, 
  getOrCreateSpouseRelationshipAsync, 
  getOrCreateProfileFamilyAsync,
  isChildRelationship, 
  isSpouseRelationship, 
  isParentRelationship,
  getNewProfileSearchRelatedPlaceholder
} from '@/manage/profiles/ProfileManager'
import { isDefined } from '@/util/TypeScriptUtil'
import { useTokenManager } from '@/auth/TokenManager'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import PersonSelector from '@/manage/PersonSelector.vue'
import FamilySelector from '@/manage/FamilySelector.vue'
import ProfileRelationshipSelector from './ProfileRelationshipSelector.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileCreatorSelector',
  props: {
    relationship: {},
    chooseGender: { type: Boolean },
    relatedItemId: {},
    useExisting: { type: Boolean },
    existingPersonId: {},
    disabled: { type: Boolean },
    validateExisting: { type: Function }
  },
  emits: [
  'update:useExisting',
  'update:existingPersonId',
],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;

// This component can be used to select or create a profile without any pre-existing relationship context, or
// to select or create a profile for the purpose of creating a specific type of relationship. The combination of 
// these two overlapping but not entirely compatible use cases makes the component a bit convoluted, so it may 
// be worth refactoring into two separate components.




const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()
const tokenManager = useTokenManager()

const useExistingRef = ref(true)
const selectedPersonIdRef = ref<string>()
const existingSelectorRef = ref<InstanceType<typeof PersonSelector>>()
const existingPersonErrorRef = ref<string>()
const existingPersonIdRef = ref<string>()
const newRelationshipRef = ref(NewProfileRelationship.Son)
const relatedItemIdRef = ref<string>()
const relatedItemErrorRef = ref<string>()
const genderRef = ref<Gender>()
const givenRef = ref("")
const surnameRef = ref("")

const canSelectExistingRef = computed(() => !props.disabled)
const canEditNewRef = computed(() => !props.disabled)

const relatedItemTypeRef = computed(() => isChildRelationship(newRelationshipRef.value) ? ItemType.Family : ItemType.Person)
const relatedItemSearchPlaceholderRef = computed(() => getNewProfileSearchRelatedPlaceholder(newRelationshipRef.value))
const showChooseRelationshipRef = computed(() => !props.relationship)
const showChooseRelatedRef = computed(() => !props.relatedItemId)
const genderValues = [
  { value: Gender.Male, name: Gender.Male },
  { value: Gender.Female, name: Gender.Female },
]
const hasNameRef = computed(() => !!givenRef.value.trim() || !!surnameRef.value.trim())
const isValidRef = computed(() => !!(useExistingRef.value 
  ? (existingPersonIdRef.value && !existingPersonErrorRef.value)
  : (relatedItemIdRef.value && !relatedItemErrorRef.value && hasNameRef.value)))

__expose({
  isValid: isValidRef,
  reset,
  getOrCreateProfileAsync,
  getOrCreateRelationshipAsync,
})

watch(props, () =>{
  if (props.relationship) {
    // this should be set if the relationship is pre-defined, and the user should not be able to change it
    newRelationshipRef.value = props.relationship
    genderRef.value = getNewProfileRelationshipGender(props.relationship)
  }

  if (props.relatedItemId) {
    // this should be set if the related person or family is pre-defined, and the user does not need to see or change it
    if (!props.relationship)
      throw 'Cannot set related item without a pre-defined relationship'

    relatedItemIdRef.value = props.relatedItemId
  }
}, { immediate: true })

watch(() => props.useExisting, () => { if (props.useExisting) useExistingRef.value = props.useExisting })
watch(() => props.existingPersonId, () => { if (props.existingPersonId) existingPersonIdRef.value = props.existingPersonId })
watch(useExistingRef, () => emit('update:useExisting', useExistingRef.value))
watch(existingPersonIdRef, () => emit('update:existingPersonId', existingPersonIdRef.value))

async function getSelectableExistingPersonsAsync(vps: ViewPerson[]) {
  // only include managed profiles that are not assumed deceased
  const groupIds = new Set<string>()
  vps.forEach(vp => vp.groupIds.forEach(gid => groupIds.add(gid)))

  const personGroups = await dataGroupStore.getGroupListAsync([...groupIds])
  const profilePersonIds = new Set<string>(personGroups
    .filter(g => g && g.groupType == DataGroupType.Profile)
    .map(g => g.startItemId!))

    return vps.filter(vp => vp.recentlyLiving && vp.matchIds.some(id => profilePersonIds.has(id)))
}

watch(selectedPersonIdRef, async () => {
  existingPersonIdRef.value = ''
  existingPersonErrorRef.value = ''
  
  if (!useExistingRef.value || !selectedPersonIdRef.value)
    return // irrelevant or obviously invalid, so no error message

  const existingPerson = await viewPersonStore.getAsyncPerson(selectedPersonIdRef.value)?.whenLoadCompleted
  if (!existingPerson) {
    existingPersonErrorRef.value = 'Person not found' // should never happen
    return
  }
  
  const personGroups = await dataGroupStore.getGroupListAsync(existingPerson.groupIds)
  if (!personGroups.every(isDefined)) {
    existingPersonErrorRef.value = 'Profile not found' // should never happen
    return
  }

  const profilePersonGroups = personGroups
    .filter(g => g.groupType == DataGroupType.Profile && existingPerson.matchIds.includes(g.startItemId!))
  if (profilePersonGroups.length == 0) {
    existingPersonErrorRef.value = 'This person does not have a managed profile.' // should never happen
    return
  }

  const otherError = props.validateExisting?.(selectedPersonIdRef.value)
  if (otherError) {
    existingPersonErrorRef.value = otherError
    return
  }

  // TODO: Allow recruiter to invite people whose profiles are owned by FG owner

  const ownedProfilePersonGroups = profilePersonGroups.filter(g => g.ownerId == tokenManager.current.userId)
  // if (ownedProfilePersonGroups.length == 0) {
  //   existingPersonErrorRef.value = 'Select someone with a managed profile that you own.'
  //   return
  // }
  if (ownedProfilePersonGroups.length > 1) {
    existingPersonErrorRef.value = `This person has more than one managed profile.`
    return
  }

  existingPersonIdRef.value = selectedPersonIdRef.value
})

watch(newRelationshipRef, () => relatedItemIdRef.value = undefined)

watch([newRelationshipRef, relatedItemIdRef], async () => {
  relatedItemErrorRef.value = ''

  if (!relatedItemIdRef.value)
    return // obviously invalid

  if (isParentRelationship(newRelationshipRef.value)) {
    const relatedGroupId = CompositeId.getGroupId(relatedItemIdRef.value)
    const relatedGroup = await dataGroupStore.getAsyncGroup(relatedGroupId, LoadMode.EnsureLoaded)?.whenLoadCompleted
    if (relatedGroup?.ownerId != tokenManager.current.userId) {
      relatedItemErrorRef.value = "You don't have permission to create a new parent for this profile."
      return
    }
  }
})

async function getSelectableRelatedPersonsAsync(vps: ViewPerson[]) {
  return vps.filter(vp => !vp.assumeDeceased)
}

async function getOrCreateProfileAsync() {
  if (useExistingRef.value) {
    if (!existingPersonIdRef.value)
      throw 'No existing profile person selected'

    return existingPersonIdRef.value
  }
  else {
    const np = {
      gender: props.chooseGender ? genderRef.value : getNewProfileRelationshipGender(newRelationshipRef.value),
      givenName: givenRef.value.trim(),
      surname: surnameRef.value.trim(),
    }
    return await createProfilePersonAsync(np)
  }
}

async function getOrCreateRelationshipAsync(profilePersonId: string) {
  if (!relatedItemIdRef.value)
    throw 'Cannot create profile because no related person or family selected'

  if (isChildRelationship(newRelationshipRef.value)) {
    if (relatedItemTypeRef.value != ItemType.Family)
      throw 'Related item when creating a child profile must be a family'

    await getOrCreateChildRelationshipAsync(profilePersonId, relatedItemIdRef.value)
  }
  else {
    if (relatedItemTypeRef.value != ItemType.Person)
      throw 'Related item when creating a spouse or parent profile must be a person'

    if (isSpouseRelationship(newRelationshipRef.value)) {
      const spouseRole = newRelationshipRef.value == NewProfileRelationship.Husband 
        ? FamilyRelationshipRole.Father
        : FamilyRelationshipRole.Mother
      await getOrCreateSpouseRelationshipAsync(relatedItemIdRef.value, profilePersonId, spouseRole)
    }
    else {
      // create family in (new) parent profile group
      const profileGroupId = CompositeId.getGroupId(profilePersonId)!
      const fatherId = newRelationshipRef.value == NewProfileRelationship.Father ? profilePersonId : undefined
      const motherId = newRelationshipRef.value == NewProfileRelationship.Mother ? profilePersonId : undefined
      const profileFamily = await getOrCreateProfileFamilyAsync(profileGroupId, fatherId, motherId)

      // create child relationship in child profile group
      await getOrCreateChildRelationshipAsync(relatedItemIdRef.value, profileFamily.id!)
    }
  }

  return profilePersonId
}

async function reset() {
  useExistingRef.value = true
  selectedPersonIdRef.value = undefined
  existingSelectorRef.value?.reset()
  existingPersonErrorRef.value = ''
  newRelationshipRef.value = NewProfileRelationship.Son
  relatedItemIdRef.value = undefined
  relatedItemErrorRef.value = ''
  genderRef.value = undefined
  givenRef.value = ''
  surnameRef.value = ''

  await nextTick()
  existingSelectorRef.value?.focus()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          _withDirectives(_createElementVNode("input", {
            type: "radio",
            class: "form-check-input",
            value: true,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((useExistingRef).value = $event)),
            disabled: props.disabled
          }, null, 8, _hoisted_5), [
            [_vModelRadio, useExistingRef.value]
          ]),
          _createTextVNode(" Select an existing profile: ")
        ])
      ]),
      _createVNode(PersonSelector, {
        ref_key: "existingSelectorRef",
        ref: existingSelectorRef,
        modelValue: selectedPersonIdRef.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedPersonIdRef).value = $event)),
        disabled: !canSelectExistingRef.value,
        "post-filter": getSelectableExistingPersonsAsync,
        onFocus: _cache[2] || (_cache[2] = ($event: any) => (useExistingRef.value = true))
      }, null, 8, ["modelValue", "disabled"]),
      (existingPersonErrorRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(existingPersonErrorRef.value), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("label", _hoisted_9, [
          _withDirectives(_createElementVNode("input", _mergeProps({
            type: "radio",
            class: "form-check-input",
            value: false
          }, { disabled: props.disabled }, {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((useExistingRef).value = $event))
          }), null, 16), [
            [_vModelRadio, useExistingRef.value]
          ]),
          (showChooseRelationshipRef.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(" Create a new profile as a "),
                _createVNode(ProfileRelationshipSelector, {
                  modelValue: newRelationshipRef.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newRelationshipRef).value = $event)),
                  disabled: props.disabled,
                  onFocus: _cache[5] || (_cache[5] = ($event: any) => (useExistingRef.value = false))
                }, null, 8, ["modelValue", "disabled"]),
                _createTextVNode(" of: ")
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(" Create a new profile ")
              ], 64))
        ])
      ]),
      (showChooseRelatedRef.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (relatedItemTypeRef.value == _unref(ItemType).Person)
              ? (_openBlock(), _createBlock(PersonSelector, {
                  key: 0,
                  modelValue: relatedItemIdRef.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((relatedItemIdRef).value = $event)),
                  placeholder: relatedItemSearchPlaceholderRef.value,
                  disabled: !canEditNewRef.value,
                  "post-filter": getSelectableRelatedPersonsAsync,
                  onFocus: _cache[7] || (_cache[7] = ($event: any) => (useExistingRef.value = false))
                }, null, 8, ["modelValue", "placeholder", "disabled"]))
              : (_openBlock(), _createBlock(FamilySelector, {
                  key: 1,
                  modelValue: relatedItemIdRef.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((relatedItemIdRef).value = $event)),
                  placeholder: relatedItemSearchPlaceholderRef.value,
                  disabled: !canEditNewRef.value,
                  "living-only": "",
                  onFocus: _cache[9] || (_cache[9] = ($event: any) => (useExistingRef.value = false))
                }, null, 8, ["modelValue", "placeholder", "disabled"]))
          ], 64))
        : _createCommentVNode("", true),
      (props.chooseGender)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("label", _hoisted_11, [
              _hoisted_12,
              _withDirectives(_createElementVNode("select", {
                class: "form-select",
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((genderRef).value = $event)),
                disabled: !canEditNewRef.value
              }, [
                _hoisted_14,
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(genderValues, (v) => {
                  return _createElementVNode("option", {
                    value: v.value,
                    key: v.value
                  }, _toDisplayString(v.name), 9, _hoisted_15)
                }), 64))
              ], 8, _hoisted_13), [
                [_vModelSelect, genderRef.value]
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      (relatedItemErrorRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(relatedItemErrorRef.value), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("label", _hoisted_18, [
          _hoisted_19,
          _withDirectives(_createElementVNode("input", {
            type: "text",
            id: "givenInput",
            class: "form-control",
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((givenRef).value = $event)),
            disabled: useExistingRef.value || !canEditNewRef.value
          }, null, 8, _hoisted_20), [
            [_vModelText, givenRef.value]
          ])
        ])
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("label", _hoisted_21, [
          _hoisted_22,
          _withDirectives(_createElementVNode("input", {
            type: "text",
            id: "surnameInput",
            class: "form-control",
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((surnameRef).value = $event)),
            disabled: useExistingRef.value || !canEditNewRef.value
          }, null, 8, _hoisted_23), [
            [_vModelText, surnameRef.value]
          ])
        ])
      ]),
      _hoisted_24
    ])
  ]))
}
}

})